/**
 * The vendor console's contract (PLAN §13 phase 7b items 1 and 5, D13, D15,
 * D27): tenants with their counts and their suspension, the cross-tenant ops
 * view (nodes, live productions, alarms), releases and the internal pins,
 * the vendor's own activity log, and announcements — vendor-authored notices
 * and incidents shown in-app to the tenants they target and, when public, on
 * the status page (`statusPage.ts`).
 *
 * The routes are `vendorRoutes.ts`. Every one under `/vendor` is the vendor
 * tenant's only (`vendor.ops` to read, `vendor.admin` to write). Two are not:
 * `GET /announcements` (any signed-in user: what is shown to THEIR tenant,
 * without the targeting) and `GET /public/announcements` (no credential: the
 * status page's public incidents).
 *
 * **A tenant's status.** `suspended` refuses the tenant's users (every route
 * answers `suspended`, 403, naming the reason; the WebSocket refuses to
 * connect) and its nodes' site links (refused with the reason; a node keeps
 * running its productions locally — docs/runtime.md). The vendor tenant can
 * never be suspended.
 */
import { ulidSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';
import { releasePinSchema, sha256HexSchema } from './releases.js';
import { siteLinkStateSchema, siteNodeRoleSchema } from './sites.js';
import { siteIdSchema, studioIdSchema } from './studios.js';
import { emailSchema, tenantIdSchema, userIdSchema } from './tenancy.js';
import { witnessInForceSchema } from './witness.js';

const isoSchema = z.iso.datetime({ offset: true });

// ─── tenants ───

export const TENANT_STATUSES = ['active', 'suspended'] as const;
export const tenantStatusSchema = z.enum(TENANT_STATUSES);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

/** A tenant's usage and footprint, counted when read. */
export const tenantCountsSchema = z
  .object({
    users: z.number().int().nonnegative(),
    sites: z.number().int().nonnegative(),
    /** Active (not revoked) nodes across its sites. */
    nodes: z.number().int().nonnegative(),
    /** Productions whose status is `active` (on-prem and cloud alike). */
    liveProductions: z.number().int().nonnegative(),
    /** Cloud-production hours metered this calendar month (UTC; PLAN §9.6). */
    cloudProductionHoursThisMonth: z.number().nonnegative(),
  })
  .strict();
export type TenantCounts = z.infer<typeof tenantCountsSchema>;

export const vendorTenantSchema = z
  .object({
    tenantId: tenantIdSchema,
    name: z.string(),
    createdAt: z.string(),
    status: tenantStatusSchema,
    suspendedAt: isoSchema.nullable(),
    suspendedReason: z.string().nullable(),
    isVendor: z.boolean(),
    counts: tenantCountsSchema,
  })
  .strict();
export type VendorTenant = z.infer<typeof vendorTenantSchema>;

export const tenantPathSchema = z.object({ tenantId: tenantIdSchema.describe('The tenant’s id.') }).strict();

export const suspendTenantRequestSchema = z
  .object({
    /** Shown to the tenant's users and nodes with every refusal. */
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type SuspendTenantRequest = z.infer<typeof suspendTenantRequestSchema>;

export const reactivateTenantRequestSchema = z
  .object({
    /** Why, for the vendor's activity log. */
    note: z.string().max(500),
  })
  .strict();
export type ReactivateTenantRequest = z.infer<typeof reactivateTenantRequestSchema>;

// ─── alarms (D27; the conditions of `@acp/tenant-alerts/conditions.js`) ───

/** The conditions the ops view evaluates on the latest status (PLAN §11.1's alarms that are states, not events). */
export const ALARM_CONDITIONS = ['server-offline-live', 'standby-missing', 'cert-expiring', 'clock-drift', 'device-disconnected-on-air'] as const;
export const alarmConditionSchema = z.enum(ALARM_CONDITIONS);
export type AlarmCondition = z.infer<typeof alarmConditionSchema>;

export const alarmSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type AlarmSeverity = z.infer<typeof alarmSeveritySchema>;

export const alarmSchema = z
  .object({
    type: alarmConditionSchema,
    /** Stable across evaluations while the condition holds (dedup key). */
    key: z.string().min(1).max(300),
    severity: alarmSeveritySchema,
    tenantId: tenantIdSchema,
    siteId: z.string(),
    studioId: z.string().nullable(),
    /** What it is about: a node's label, a production id, a device id. */
    subject: z.string(),
    summary: z.string(),
    detail: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
  })
  .strict();
export type Alarm = z.infer<typeof alarmSchema>;

// ─── the ops view ───

export const vendorNodeSchema = z
  .object({
    tenantId: tenantIdSchema,
    siteId: siteIdSchema,
    siteName: z.string(),
    nodeId: z.string(),
    label: z.string(),
    role: siteNodeRoleSchema,
    linkState: siteLinkStateSchema,
    lastSeen: isoSchema.nullable(),
    witness: witnessInForceSchema.nullable(),
    packageHashes: z.array(z.string()),
    certExpiresAt: isoSchema.nullable(),
    clockDriftMs: z.number().nullable(),
    healthReportedAt: isoSchema.nullable(),
    /** Productions the node's last health report named, with its role for each. */
    productions: z.array(z.object({ productionId: z.string(), role: z.enum(['active', 'standby']) }).strict()),
  })
  .strict();
export type VendorNode = z.infer<typeof vendorNodeSchema>;

/** `single`: no pair (a production outside main/backup); otherwise the playout state's `redundancy.protection`. */
export const vendorProtectionSchema = z.enum(['protected', 'syncing', 'unprotected', 'single', 'unknown']);

export const vendorProductionSchema = z
  .object({
    tenantId: tenantIdSchema,
    productionId: ulidSchema,
    studioId: studioIdSchema,
    siteId: z.string().nullable(),
    activeNode: z.string().nullable(),
    /** Something is on air (the playout state has a current part). */
    onAir: z.boolean(),
    protection: vendorProtectionSchema,
    /** The cloud's last playout snapshot is older than the site link's stale window, or there is none. */
    stale: z.boolean(),
    devices: z.object({ total: z.number().int().nonnegative(), disconnected: z.number().int().nonnegative() }).strict(),
    packageHash: z.string().nullable(),
  })
  .strict();
export type VendorProduction = z.infer<typeof vendorProductionSchema>;

export const vendorOpsResponseSchema = z
  .object({
    generatedAt: isoSchema,
    tenants: z.array(z.object({ tenantId: tenantIdSchema, name: z.string(), status: tenantStatusSchema }).strict()),
    nodes: z.array(vendorNodeSchema),
    productions: z.array(vendorProductionSchema),
    alarms: z.array(alarmSchema),
  })
  .strict();
export type VendorOpsResponse = z.infer<typeof vendorOpsResponseSchema>;

export const vendorTenantResponseSchema = z.object({ tenant: vendorTenantSchema }).strict();
export const listVendorTenantsResponseSchema = z.object({ tenants: z.array(vendorTenantSchema) }).strict();
export const vendorTenantDetailResponseSchema = z
  .object({
    tenant: vendorTenantSchema,
    sites: z.array(z.object({ siteId: siteIdSchema, name: z.string(), kind: z.enum(['onprem', 'cloud']) }).strict()),
    nodes: z.array(vendorNodeSchema),
    productions: z.array(vendorProductionSchema),
    alarms: z.array(alarmSchema),
  })
  .strict();
export type VendorTenantDetail = z.infer<typeof vendorTenantDetailResponseSchema>;

// ─── releases and pins (D15) ───

export const vendorReleaseSchema = z
  .object({
    listHash: sha256HexSchema,
    commit: z.string(),
    /** When its list was published (the store's timestamp), or null if unknown. */
    publishedAt: isoSchema.nullable(),
    packages: z.array(z.object({ name: z.string(), hash: sha256HexSchema }).strict()),
  })
  .strict();
export type VendorRelease = z.infer<typeof vendorReleaseSchema>;

export const rolloutSourceSchema = z.enum(['site-pin', 'stage-pin', 'latest']);

export const rolloutNodeSchema = z
  .object({
    tenantId: tenantIdSchema,
    siteId: siteIdSchema,
    nodeId: z.string(),
    label: z.string(),
    role: siteNodeRoleSchema,
    linkState: siteLinkStateSchema,
    /** What the manifest serves this node (site pin → stage pin → latest), and that list; null when nothing is published. */
    target: z.object({ source: rolloutSourceSchema, listHash: sha256HexSchema.nullable() }).strict(),
    /** The newest known release every package of which the node reports holding; null when none. */
    running: sha256HexSchema.nullable(),
    upToDate: z.boolean(),
  })
  .strict();
export type RolloutNode = z.infer<typeof rolloutNodeSchema>;

export const sitePinViewSchema = z.object({ siteId: siteIdSchema, pin: releasePinSchema }).strict();

export const vendorReleasesResponseSchema = z
  .object({
    /** The latest release (`packages.json`), or null before the first publish. */
    latest: sha256HexSchema.nullable(),
    /** The most recently published releases, newest first. */
    releases: z.array(vendorReleaseSchema),
    stagePin: releasePinSchema.nullable(),
    sitePins: z.array(sitePinViewSchema),
    rollout: z.array(rolloutNodeSchema),
  })
  .strict();
export type VendorReleasesResponse = z.infer<typeof vendorReleasesResponseSchema>;

export const setPinRequestSchema = z.object({ listHash: sha256HexSchema, note: z.string().max(512) }).strict();
export type SetPinRequest = z.infer<typeof setPinRequestSchema>;
export const pinResponseSchema = z.object({ pin: releasePinSchema.nullable() }).strict();
export const sitePinPathSchema = z.object({ siteId: siteIdSchema.describe('The site’s id.') }).strict();

// ─── the vendor's activity log ───

export const VENDOR_ACTIONS = [
  'tenant.create',
  'tenant.suspend',
  'tenant.reactivate',
  'pin.set',
  'pin.clear',
  'announcement.create',
  'announcement.update',
  'announcement.delete',
] as const;
export const vendorActionSchema = z.enum(VENDOR_ACTIONS);
export type VendorAction = z.infer<typeof vendorActionSchema>;

export const vendorActivitySchema = z
  .object({
    activityId: z.string(),
    at: isoSchema,
    actor: z.object({ userId: userIdSchema, email: emailSchema }).strict(),
    action: vendorActionSchema,
    /** What it acted on: a tenant id, `stage` or `site:<siteId>`, an announcement id. */
    target: z.string(),
    detail: z.string(),
  })
  .strict();
export type VendorActivity = z.infer<typeof vendorActivitySchema>;

export const listVendorActivityQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(200).optional().describe('At most this many entries (default 50).'),
    before: z.string().max(200).optional().describe('The `next` cursor of a previous page.'),
  })
  .strict();
export const listVendorActivityResponseSchema = z.object({ entries: z.array(vendorActivitySchema), next: z.string().nullable() }).strict();

// ─── announcements ───

/** The status page's components (`statusPage.ts`), which an incident may name. */
export const STATUS_COMPONENTS = ['api', 'websocket', 'sign-in', 'cloud-productions'] as const;
export const statusComponentSchema = z.enum(STATUS_COMPONENTS);
export type StatusComponent = z.infer<typeof statusComponentSchema>;

export const announcementKindSchema = z.enum(['announcement', 'maintenance', 'incident']);
export type AnnouncementKind = z.infer<typeof announcementKindSchema>;
export const announcementSeveritySchema = z.enum(['info', 'minor', 'major', 'critical']);
export type AnnouncementSeverity = z.infer<typeof announcementSeveritySchema>;

const titleSchema = z.string().trim().min(1).max(200);
const bodySchema = z.string().max(5000);

export const announcementSchema = z
  .object({
    announcementId: ulidSchema,
    kind: announcementKindSchema,
    severity: announcementSeveritySchema,
    title: z.string(),
    body: z.string(),
    startsAt: isoSchema,
    /** Null: until it is ended. */
    endsAt: isoSchema.nullable(),
    /** Shown on the public status page (only when it targets every tenant). */
    public: z.boolean(),
    /** Null: every tenant. */
    tenantIds: z.array(tenantIdSchema).nullable(),
    components: z.array(statusComponentSchema),
    version: z.number().int().positive(),
    createdAt: isoSchema,
    createdBy: z.string(),
    updatedAt: isoSchema,
    updatedBy: z.string(),
  })
  .strict();
export type Announcement = z.infer<typeof announcementSchema>;

const announcementFields = {
  kind: announcementKindSchema,
  severity: announcementSeveritySchema,
  title: titleSchema,
  body: bodySchema,
  startsAt: isoSchema,
  endsAt: isoSchema.nullable(),
  public: z.boolean(),
  tenantIds: z.array(tenantIdSchema).min(1).max(500).nullable(),
  components: z.array(statusComponentSchema).max(STATUS_COMPONENTS.length),
};

/** A public announcement targets every tenant, and an end comes after the start. */
function coherent(value: { startsAt?: string | undefined; endsAt?: string | null | undefined; public?: boolean | undefined; tenantIds?: readonly string[] | null | undefined }, ctx: z.RefinementCtx): void {
  if (value.public === true && value.tenantIds !== undefined && value.tenantIds !== null) {
    ctx.addIssue({ code: 'custom', path: ['public'], message: 'a public announcement targets every tenant (tenantIds: null)' });
  }
  if (value.startsAt !== undefined && value.endsAt !== undefined && value.endsAt !== null && Date.parse(value.endsAt) <= Date.parse(value.startsAt)) {
    ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'endsAt must be after startsAt' });
  }
}

export const createAnnouncementRequestSchema = z.object(announcementFields).strict().superRefine(coherent);
export type CreateAnnouncementRequest = z.infer<typeof createAnnouncementRequestSchema>;

export const updateAnnouncementRequestSchema = z
  .object({ ...z.object(announcementFields).partial().shape, version: z.number().int().positive().describe('The version this edit was made from (409 if it moved).') })
  .strict()
  .superRefine(coherent);
export type UpdateAnnouncementRequest = z.infer<typeof updateAnnouncementRequestSchema>;

export const announcementPathSchema = z.object({ announcementId: ulidSchema.describe('The announcement’s id.') }).strict();
export const announcementResponseSchema = z.object({ announcement: announcementSchema }).strict();
export const listVendorAnnouncementsResponseSchema = z.object({ announcements: z.array(announcementSchema) }).strict();

/** What a tenant's users see: no targeting, no authorship. */
export const tenantAnnouncementSchema = z
  .object({
    announcementId: ulidSchema,
    kind: announcementKindSchema,
    severity: announcementSeveritySchema,
    title: z.string(),
    body: z.string(),
    startsAt: isoSchema,
    endsAt: isoSchema.nullable(),
    version: z.number().int().positive(),
  })
  .strict();
export type TenantAnnouncement = z.infer<typeof tenantAnnouncementSchema>;
export const listAnnouncementsResponseSchema = z.object({ announcements: z.array(tenantAnnouncementSchema) }).strict();

/** The status page's view of a public announcement. */
export const publicAnnouncementSchema = z
  .object({
    announcementId: ulidSchema,
    kind: announcementKindSchema,
    severity: announcementSeveritySchema,
    title: z.string(),
    body: z.string(),
    startsAt: isoSchema,
    endsAt: isoSchema.nullable(),
    components: z.array(statusComponentSchema),
    updatedAt: isoSchema,
  })
  .strict();
export type PublicAnnouncement = z.infer<typeof publicAnnouncementSchema>;
export const listPublicAnnouncementsResponseSchema = z.object({ announcements: z.array(publicAnnouncementSchema) }).strict();

/** How long an ended public announcement stays on the status page. */
export const PUBLIC_ANNOUNCEMENT_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

/** Whether an announcement is in force at `nowMs` (started, not ended). Pure; shared by the cloud and its tests. */
export function isInForce(announcement: Pick<Announcement, 'startsAt' | 'endsAt'>, nowMs: number): boolean {
  if (Date.parse(announcement.startsAt) > nowMs) return false;
  return announcement.endsAt === null || Date.parse(announcement.endsAt) > nowMs;
}

/** Whether `tenantId` is one an announcement targets. */
export function targets(announcement: Pick<Announcement, 'tenantIds'>, tenantId: string): boolean {
  return announcement.tenantIds === null || announcement.tenantIds.includes(tenantId);
}
